import React, { useState, useEffect, useCallback } from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Callout from '@/components/General/Callout';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import FieldLabel from './FieldLabel';
import FormProfileView from '@/components/Forms/FormProfileView';
import { Query } from '@/utils/react-query/Queries';

/**
 * `profile` schema field — selects a single existing Reach person.
 * Wire value is exactly `null` or `{ personID }`; the picked person object is
 * local display state only and is never serialized.
 *
 * renderMode:
 * - 'fill': interactive SheetSelector (directory permission required).
 * - 'preview': disabled selector skeleton.
 * - 'submission': read-only identity + View card for permitted reviewers.
 */
const ProfileField = ({ field, value, onChange, disabled, renderMode = 'fill', formContext = {}, loggedInUser }) => {
    const fieldId = field.fieldId;
    const viewID = field.config?.viewID || null;
    const showToRespondent = field.config?.showViewToRespondent === true;
    const personID = value && typeof value === 'object' ? value.personID || null : null;

    // Local display object — holds the picked People record for the trigger UI.
    // Cleared whenever the wire personID no longer matches it — an external
    // change (409 reset, initialValues hydration) must never show person A
    // under person B's answer.
    const [selectedPerson, setSelectedPerson] = useState(null);
    useEffect(() => {
        setSelectedPerson((prev) => (personID && prev?.id === personID ? prev : null));
    }, [personID]);

    // Admins get `View People` via assignPermissions; anything else (anon,
    // member-facing) must not mount the directory picker. Repeat instances get
    // loggedInUser=null (no autofill target) — the viewer's permission still
    // applies via formContext.
    const canViewPeople = !!(loggedInUser?.can?.('View People') ?? formContext.viewerCanViewPeople);

    // Restore display object when a draft value exists without a picked person
    // (e.g. retry after a failed submit). Directory permission still required.
    const personQuery = Query.people.basicById({
        id: personID,
        options: { enabled: !!personID && !selectedPerson && canViewPeople },
    });
    useEffect(() => {
        if (personQuery.data && !selectedPerson) setSelectedPerson(personQuery.data);
    }, [personQuery.data]);

    const handleSelect = useCallback((person) => {
        setSelectedPerson(person || null);
        onChange?.(person?.id ? { personID: person.id } : null);
    }, [onChange]);

    if (renderMode === 'submission') {
        return (
            <View>
                <FieldLabel
                    label={field.label}
                    description={field.description}
                    testID={`form-field-${fieldId}-label`}
                />
                {personID ? (
                    <View style={styles.identityRow} testID={`profile-${fieldId}-identity`}>
                        <Avatar size={28} profile={personQuery.data} disableBorder />
                        <Text style={styles.identityText}>
                            {personQuery.data
                                ? `${personQuery.data.firstName || ''} ${personQuery.data.lastName || ''}`.trim()
                                : 'Profile selected'}
                        </Text>
                    </View>
                ) : (
                    <Text style={styles.muted}>No profile selected.</Text>
                )}
                {/* Submission card renders for permitted reviewers regardless of
                    the respondent-facing showViewToRespondent setting. The key
                    remount guarantees a stale card can never linger under a new
                    person. */}
                {!!viewID && !!personID && (
                    <FormProfileView
                        key={`${viewID}:${personID}`}
                        viewID={viewID}
                        personID={personID}
                        testID={`profile-${fieldId}-card`}
                    />
                )}
            </View>
        );
    }

    // fill / preview
    if (formContext.formIsPublic) {
        // Defensive: the schema guard should prevent this form from being
        // public at all — never mount a directory picker for anonymous users.
        return (
            <View>
                <FieldLabel label={field.label} required={field.required} description={field.description} />
                <Callout type="warning" message="Profile selection isn't available on public forms." />
            </View>
        );
    }

    return (
        <View>
            <FieldLabel
                label={field.label}
                required={field.required}
                description={field.description}
                testID={`form-field-${fieldId}-label`}
            />
            {!canViewPeople ? (
                <View style={styles.denied} testID={`profile-${fieldId}-denied`}>
                    <Unicon name="lock-closed-outline" size={16} colorVariant="textSecondary" />
                    <Text style={styles.deniedText}>
                        Profile selection requires permission to view people.
                        {field.required ? ' This form cannot be submitted without it.' : ''}
                    </Text>
                </View>
            ) : (
                <View testID={`profile-${fieldId}-selector`}>
                    <SheetSelector
                        title={field.label || 'Profile'}
                        type="people"
                        multiple={false}
                        placeholder="Choose a person"
                        value={selectedPerson}
                        onChange={handleSelect}
                        disabled={disabled || renderMode === 'preview'}
                    />
                </View>
            )}
            {/* Respondent-facing card — only after a selection, only when a View
                is configured and the creator opted in. key={personID} remounts
                on switch so a late-arriving card for person A never shows under
                person B. */}
            {canViewPeople && !!personID && !!viewID && showToRespondent && (
                <FormProfileView
                    key={`${viewID}:${personID}`}
                    viewID={viewID}
                    personID={personID}
                    testID={`profile-${fieldId}-card`}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness }) => ({
    identityRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        paddingVertical: 4,
    },
    identityText: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: typography.body,
    },
    denied: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        backgroundColor: colors.formsContainer,
        borderRadius: roundness.md,
        padding: spacing.sm,
    },
    deniedText: {
        flex: 1,
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
    },
    muted: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
    },
}));

export default ProfileField;
